import * as React from "react";
import { Heading1 } from "@/components/Typography";
import { cn } from "@/lib/utils/common";

interface PageHeaderProps {
  className?: string;
  children: React.ReactNode;
  /**
   * Whether to include the standard divider line below the header
   * @default true
   */
  showDivider?: boolean;
}

/**
 * PageHeader provides consistent header layout across pages.
 * Use compound components (PageHeader.Title, PageHeader.Description, PageHeader.Actions)
 * for flexible content composition.
 */
export function PageHeader({ className, children, showDivider = true }: PageHeaderProps) {
  return (
    <>
      <div className={cn("flex items-center justify-between px-4 py-2", className)}>{children}</div>
      {showDivider && <hr className="border-border" />}
    </>
  );
}

interface PageHeaderContentProps {
  children: React.ReactNode;
  className?: string;
}

/**
 * Container for the main header content (title + description)
 */
function PageHeaderContent({ children, className }: PageHeaderContentProps) {
  return <div className={cn("space-y-1", className)}>{children}</div>;
}

interface PageHeaderTitleProps {
  children: React.ReactNode;
  className?: string;
}

/**
 * Standard page title using Heading1 component
 */
function PageHeaderTitle({ children, className }: PageHeaderTitleProps) {
  return <Heading1 className={className}>{children}</Heading1>;
}

interface PageHeaderDescriptionProps {
  children: React.ReactNode;
  className?: string;
}

/**
 * Page description/subtitle text
 */
function PageHeaderDescription({ children, className }: PageHeaderDescriptionProps) {
  return <p className={cn("text-muted-foreground", className)}>{children}</p>;
}

interface PageHeaderActionsProps {
  children: React.ReactNode;
  className?: string;
}

/**
 * Container for header action buttons or other interactive elements
 */
function PageHeaderActions({ children, className }: PageHeaderActionsProps) {
  return <div className={cn("flex items-center gap-2", className)}>{children}</div>;
}

// Attach compound components
PageHeader.Content = PageHeaderContent;
PageHeader.Title = PageHeaderTitle;
PageHeader.Description = PageHeaderDescription;
PageHeader.Actions = PageHeaderActions;
